import { createSelector, lruMemoize } from 'reselect';
import _ from 'lodash';
import { shallowEqual } from 'react-redux';
import { localeApi } from 'app/locale/api';
import {
  LocaleResources, LocaleResourcesOrder, LocaleResult, TranslationDictionary,
} from 'app/locale/types';
import type { RootState } from 'store';
import { generateMapByOrder } from 'utils/utils';
import { normalizeEnumLikeMap } from 'utils/helpers';

const selectGetLocaleResourcesResult = localeApi.endpoints.getLocaleResources.select();

export const selectLocaleFulFilledTime = (state: RootState) => selectGetLocaleResourcesResult(state)?.fulfilledTimeStamp;

export const selectLocaleResources = (state: RootState) => selectGetLocaleResourcesResult(state)?.data;

const memoizeOptions = {
  equalityCheck: shallowEqual,
  maxSize: 10,
};

export const makeSelectLocaleResource = <
  T extends LocaleResources,
  O extends LocaleResourcesOrder | undefined = undefined
>(
    resourceObj: T,
    order?: O,
  ) => createSelector(
    selectLocaleResources,
    (resources): LocaleResult<T, O> => {
      const resourceKeys = Object.keys(resourceObj) as Array<keyof T>;
      const res = {} as LocaleResult<T, O>;

      resourceKeys.forEach((key) => {
        const curr = key as keyof LocaleResources;
        const normalized = normalizeEnumLikeMap(
          resourceObj[curr] as Record<string, string | number>,
        );
        const currentEnum = _.invert(normalized) as TranslationDictionary;
        const remoteValues = resources?.[curr] ?? {};
        if (order && (order as LocaleResourcesOrder)[curr]) {
          const combined = { ...currentEnum, ...remoteValues };
          (res[key] as Map<string|number, string>) = generateMapByOrder(
            (order as LocaleResourcesOrder)[curr],
            combined,
          );
        } else {
          const obj: Record<string, string> = {};
          Object.keys(currentEnum).forEach((el) => {
            obj[el] = String(remoteValues[el] ?? currentEnum[el]);
          });
          (res[key] as Record<string, string>) = obj;
        }
      });
      return res;
    },
    {
      memoize: lruMemoize,
      memoizeOptions,
    },
  );
